/**
 * CreditCardStatementModal - Import credit card statements from PDF/Excel files
 */

import { useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useCashflowStore } from '../../stores/cashflowStore';
import {
  X,
  Upload,
  FileSpreadsheet,
  FileText,
  Image,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Square,
  CheckSquare,
  Calendar,
  CreditCard,
  Building2,
  Banknote,
  Info,
  ShoppingBag,
  Utensils,
  Car,
  Film,
  Fuel,
  Heart,
  Plane,
  Receipt,
  Repeat,
  MoreHorizontal,
  ShoppingCart,
  Wallet,
} from 'lucide-react';

// Category configuration
const CATEGORY_CONFIG = {
  food: { label: 'Yemek', icon: Utensils, color: 'text-orange-400 bg-orange-400/20' },
  groceries: { label: 'Market', icon: ShoppingCart, color: 'text-green-400 bg-green-400/20' },
  shopping: { label: 'Alışveriş', icon: ShoppingBag, color: 'text-pink-400 bg-pink-400/20' },
  transport: { label: 'Ulaşım', icon: Car, color: 'text-blue-400 bg-blue-400/20' },
  fuel: { label: 'Akaryakıt', icon: Fuel, color: 'text-yellow-400 bg-yellow-400/20' },
  entertainment: { label: 'Eğlence', icon: Film, color: 'text-purple-400 bg-purple-400/20' },
  subscriptions: { label: 'Abonelik', icon: Repeat, color: 'text-cyan-400 bg-cyan-400/20' },
  bills: { label: 'Fatura', icon: Receipt, color: 'text-gray-400 bg-gray-400/20' },
  health: { label: 'Sağlık', icon: Heart, color: 'text-red-400 bg-red-400/20' },
  travel: { label: 'Seyahat', icon: Plane, color: 'text-indigo-400 bg-indigo-400/20' },
  fee: { label: 'Masraf', icon: Banknote, color: 'text-amber-400 bg-amber-400/20' },
  cash_advance: { label: 'Nakit', icon: Wallet, color: 'text-rose-400 bg-rose-400/20' },
  other: { label: 'Diğer', icon: MoreHorizontal, color: 'text-gray-400 bg-gray-400/20' },
};

const CreditCardStatementModal = () => {
  const {
    isCreditCardModalOpen,
    closeCreditCardModal,
    isParsingStatement,
    parsedStatement,
    statementParseError,
    selectedTransactions,
    parseStatementFile,
    toggleTransactionSelection,
    selectAllTransactions,
    deselectAllTransactions,
    importStatement,
  } = useCashflowStore();

  const fileInputRef = useRef(null);

  const handleFileSelect = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await parseStatementFile(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [parseStatementFile]);

  const handleDrop = useCallback(async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      await parseStatementFile(file);
    }
  }, [parseStatementFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const formatCurrency = (amount, currency = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const getCategoryBadge = (category) => {
    const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.other;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${config.color}`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </span>
    );
  };

  if (!isCreditCardModalOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <CreditCard className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Kredi Kartı Ekstresi</h2>
                <p className="text-sm text-gray-400">PDF veya Excel dosyasından harcamaları çıkar</p>
              </div>
            </div>
            <button
              onClick={closeCreditCardModal}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Upload area - show when no statement parsed */}
            {!parsedStatement && !isParsingStatement && (
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                className="border-2 border-dashed border-gray-700 rounded-xl p-8 text-center hover:border-purple-500/50 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.xlsx,.xls,.png,.jpg,.jpeg,.webp"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Upload className="w-12 h-12 text-gray-500 mx-auto mb-4" />
                <p className="text-gray-300 mb-2">
                  Kredi kartı ekstresini sürükleyip bırakın veya tıklayın
                </p>
                <p className="text-sm text-gray-500">
                  PDF, Excel ve görsel dosyaları (PNG, JPG) desteklenir
                </p>
                <div className="flex items-center justify-center gap-4 mt-6">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <FileText className="w-4 h-4" />
                    <span>PDF</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <FileSpreadsheet className="w-4 h-4" />
                    <span>Excel</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <Image className="w-4 h-4" />
                    <span>Görsel</span>
                  </div>
                </div>
              </div>
            )}

            {/* Loading state */}
            {isParsingStatement && !parsedStatement && (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-10 h-10 text-purple-400 animate-spin mb-4" />
                <p className="text-gray-300">Ekstre analiz ediliyor...</p>
                <p className="text-sm text-gray-500 mt-1">Bu işlem birkaç saniye sürebilir</p>
              </div>
            )}

            {/* Error state */}
            {statementParseError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-red-400 font-medium">Hata</p>
                    <p className="text-red-300 text-sm mt-1">{statementParseError}</p>
                  </div>
                </div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-4 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors"
                >
                  Tekrar Dene
                </button>
              </div>
            )}

            {/* Parsed statement */}
            {parsedStatement && (
              <div className="space-y-4">
                {/* Card & Statement Summary */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Card info */}
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-purple-500/20 rounded-lg">
                        <Building2 className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <h3 className="font-medium text-white">
                          {parsedStatement.cardInfo?.bankName || 'Banka'}
                        </h3>
                        <p className="text-sm text-gray-400">
                          {parsedStatement.cardInfo?.cardName || 'Kredi Kartı'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <CreditCard className="w-4 h-4" />
                      <span className="font-mono">
                        **** **** **** {parsedStatement.cardInfo?.cardNumberMasked?.slice(-4) || 'XXXX'}
                      </span>
                    </div>
                  </div>

                  {/* Statement summary */}
                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-gray-400">Dönem</span>
                      <span className="text-sm text-gray-300">
                        {parsedStatement.statementPeriod?.startDate && parsedStatement.statementPeriod?.endDate
                          ? `${format(new Date(parsedStatement.statementPeriod.startDate), 'd MMM', { locale: tr })} - ${format(new Date(parsedStatement.statementPeriod.endDate), 'd MMM yyyy', { locale: tr })}`
                          : '-'
                        }
                      </span>
                    </div>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-gray-400">Son Ödeme</span>
                      <span className="text-sm text-gray-300">
                        {parsedStatement.statementPeriod?.dueDate
                          ? format(new Date(parsedStatement.statementPeriod.dueDate), 'd MMMM yyyy', { locale: tr })
                          : '-'
                        }
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-700">
                      <span className="text-gray-400">Toplam Borç</span>
                      <span className="text-lg font-semibold text-red-400">
                        {formatCurrency(parsedStatement.summary?.totalBalance || 0, parsedStatement.summary?.currency)}
                      </span>
                    </div>
                    {parsedStatement.summary?.minimumPayment && (
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm text-gray-400">Asgari Ödeme</span>
                        <span className="text-sm text-amber-400">
                          {formatCurrency(parsedStatement.summary.minimumPayment, parsedStatement.summary?.currency)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Warnings */}
                {parsedStatement.warnings?.length > 0 && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-amber-300">{parsedStatement.warnings.join(', ')}</p>
                    </div>
                  </div>
                )}

                {/* Selection controls */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={selectAllTransactions}
                      className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      Tümünü Seç
                    </button>
                    <button
                      onClick={deselectAllTransactions}
                      className="px-3 py-1.5 text-sm text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      Temizle
                    </button>
                  </div>
                  <span className="text-sm text-gray-400">
                    {selectedTransactions.length} / {parsedStatement.transactions?.length || 0} seçili
                  </span>
                </div>

                {/* Transactions list */}
                <div className="space-y-2 max-h-[350px] overflow-y-auto">
                  {parsedStatement.transactions?.map((transaction) => {
                    const isSelected = selectedTransactions.includes(transaction.tempId);
                    const isPayment = transaction.transactionType === 'payment';

                    return (
                      <div
                        key={transaction.tempId}
                        onClick={() => toggleTransactionSelection(transaction.tempId)}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          isSelected
                            ? 'bg-purple-500/20 border border-purple-500/30'
                            : 'bg-gray-800/30 border border-gray-700/50 hover:bg-gray-800/50'
                        }`}
                      >
                        {/* Checkbox */}
                        <div className="flex-shrink-0">
                          {isSelected ? (
                            <CheckSquare className="w-5 h-5 text-purple-400" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-500" />
                          )}
                        </div>

                        {/* Date */}
                        <div className="flex items-center gap-1.5 text-sm text-gray-400 w-24 flex-shrink-0">
                          <Calendar className="w-4 h-4" />
                          <span>
                            {format(new Date(transaction.date), 'd MMM', { locale: tr })}
                          </span>
                        </div>

                        {/* Merchant & category */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-white truncate">
                              {transaction.merchantName}
                            </span>
                            {transaction.isInstallment && (
                              <span className="text-xs text-gray-400">
                                ({transaction.installmentCurrent}/{transaction.installmentTotal})
                              </span>
                            )}
                          </div>
                          <div className="mt-1">
                            {getCategoryBadge(transaction.category)}
                          </div>
                        </div>

                        {/* Amount */}
                        <div className={`font-medium text-right ${isPayment ? 'text-green-400' : 'text-red-400'}`}>
                          {isPayment ? '+' : '-'}{formatCurrency(Math.abs(transaction.amount), transaction.currency)}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Upload another file */}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  Farklı dosya yükle
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.xlsx,.xls,.png,.jpg,.jpeg,.webp"
                  onChange={handleFileSelect}
                  className="hidden"
                />
              </div>
            )}
          </div>

          {/* Footer */}
          {parsedStatement && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800 bg-gray-900/50">
              <div className="text-sm text-gray-400">
                {selectedTransactions.length > 0 && (
                  <span className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-400" />
                    {selectedTransactions.length} işlem içe aktarılacak
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={closeCreditCardModal}
                  className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
                >
                  İptal
                </button>
                <button
                  onClick={importStatement}
                  disabled={selectedTransactions.length === 0 || isParsingStatement}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-400 text-white rounded-lg transition-colors"
                >
                  {isParsingStatement ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>İçe Aktarılıyor...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>İçe Aktar</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CreditCardStatementModal;
